import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, isImageLinkQueued, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** Cached from SSR — section morphs lose collection filter context. */
  #linkWithVariant = false;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /** Whether card links should include ?variant= (active size filter on collection). */
  get linksWithVariant() {
    return this.#linkWithVariant;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#linkWithVariant = this.dataset.sizeFilterActive === 'true';
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.#setupMediaPrefetch();
    this.#setupMobileVideoAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#prefetchObserver?.disconnect();
    this.#prefetchObserver = null;
    this.#videoVisibilityObserver?.disconnect();
    this.#videoVisibilityObserver = null;
  }

  /** @type {IntersectionObserver | null} */
  #prefetchObserver = null;

  /** @type {IntersectionObserver | null} */
  #videoVisibilityObserver = null;

  #hoverVideoShouldPlay = false;

  #setupMediaPrefetch() {
    this.#prefetchObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.#prefetchNextSlideImage();
        this.#warmHoverVideo();
        this.#prefetchObserver?.disconnect();
        this.#prefetchObserver = null;
      },
      { rootMargin: '200px 0px' },
    );

    this.#prefetchObserver.observe(this);
  }

  /**
   * Mobile has no hover, so the hover video (rendered muted + playsinline) never
   * gets a play trigger and sits on its poster. Autoplay it while its slide is
   * actually on-screen (IntersectionObserver against the viewport also accounts
   * for the horizontal slideshow clipping off-screen slides), and pause when it
   * leaves. Play is gated to mobile so desktop keeps its hover trigger even if
   * the viewport is resized after connect.
   */
  #setupMobileVideoAutoplay() {
    const video = this.#getHoverVideo();
    if (!video) return;

    this.#videoVisibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && !mediaQueryLarge.matches) {
          this.#playHoverVideo();
        } else if (!entry.isIntersecting) {
          this.#pauseHoverVideo();
        }
      },
      { threshold: 0.6 },
    );

    this.#videoVisibilityObserver.observe(video);
  }

  #prefetchNextSlideImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current ?? 0];
    const img = currentSlide?.nextElementSibling?.querySelector('img');
    if (!img?.src) return;
    if (img.srcset && isImageLinkQueued('prefetch', img.srcset)) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    if (img.srcset) {
      link.setAttribute('imagesrcset', img.srcset);
      const sizes = img.sizes?.replace(/^\s*auto\s*,\s*/i, '');
      if (sizes) link.setAttribute('imagesizes', sizes);
    } else {
      link.href = img.src;
    }
    document.head.appendChild(link);
  }

  #warmHoverVideo() {
    const video = this.#getHoverVideo();
    if (!video || video.readyState > 0) return;
    video.load();
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);
    // The pre-sale label follows the variant too (assets/pre-sale-label.js).
    this.dispatchEvent(
      new CustomEvent('pre-sale:refresh', { bubbles: true, detail: { html: event.detail.data.html } }),
    );
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    this.removeAttribute('data-no-swatch-selected');

    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const nextUrl = new URL(anchorElement.href, window.location.origin);
      const variantId = event.detail.resource?.id;

      // Section morphs drop collection context — re-apply ?variant= only when a
      // size filter was active on first paint of this card.
      if (this.#linkWithVariant && variantId != null) {
        nextUrl.searchParams.set('variant', String(variantId));
      } else {
        nextUrl.searchParams.delete('variant');
      }

      const productUrl = nextUrl.toString();
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    const video = this.#getHoverVideo();
    if (!video) return;

    if (event.detail.slide?.contains(video)) {
      this.#playHoverVideo();
    } else {
      this.#pauseHoverVideo();
    }
  };

  #playHoverVideo() {
    const video = this.#getHoverVideo();
    if (!video) return;

    this.#hoverVideoShouldPlay = true;

    // HAVE_CURRENT_DATA or better. readyState 1 is metadata only — no decodable frame — and
    // seeking from there drops the poster with nothing to replace it.
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.#rewind(video);
      video.play()?.catch(() => this.#fallBackToPoster(video));
      return;
    }

    video.addEventListener(
      'loadeddata',
      () => {
        if (this.#hoverVideoShouldPlay) video.play()?.catch(() => this.#fallBackToPoster(video));
      },
      { once: true },
    );
    video.load();
  }

  #pauseHoverVideo() {
    this.#hoverVideoShouldPlay = false;
    const video = this.#getHoverVideo();
    if (!video) return;
    video.pause();
    this.#rewind(video);
  }

  /**
   * Seeks to the start only when there is a frame to seek from — seeking clears the element's
   * show-poster flag, which would leave a video that never started showing nothing at all.
   * @param {HTMLVideoElement} video
   */
  #rewind(video) {
    if (video.currentTime === 0) return;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
    try {
      video.currentTime = 0;
    } catch {}
  }

  /**
   * When play() is rejected (iOS Low Power Mode), re-running the load algorithm restores the
   * show-poster flag so the card shows its poster instead of an empty box.
   * @param {HTMLVideoElement} video
   */
  #fallBackToPoster(video) {
    this.#hoverVideoShouldPlay = false;
    if (!video.paused) return;
    video.load();
  }

  /** @returns {HTMLVideoElement | null} */
  #getHoverVideo() {
    return this.querySelector('video.product-media__hover-video');
  }
  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * The slide the card rests on: the selected swatch's media, else the
   * initial slide.
   * @returns {HTMLElement | undefined}
   */
  #restSlide() {
    const { slideshow } = this.refs;
    if (!slideshow) return undefined;

    const id = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const variantSlide = id ? slideshow.slides?.find((slide) => slide.getAttribute('slide-id') === id) : undefined;

    return variantSlide ?? slideshow.initialSlide ?? slideshow.slides?.[0];
  }

  /**
   * Previews the hover image: the slide after the rest slide.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    // A host carousel can opt out of the hover preview (upsell carousels do).
    if (this.closest('[data-no-hover-preview]')) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    // The slideshow module loads lazily, so a card that appears under a
    // resting pointer can get this enter before the element is defined and
    // initialised. Come back once it is, after its own initial selection, if
    // the pointer is still here.
    if (typeof slideshow.select !== 'function') {
      customElements.whenDefined('slideshow-component').then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.matches(':hover')) this.previewImage(event);
          });
        });
      });
      return;
    }

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      // Addressed from the rest slide rather than `next()`: a relative step
      // from a stale `current` (an enter and a leave that didn't pair up)
      // would walk to the wrong slide, and with a looping gallery even swap
      // the rest and hover images for good.
      const slides = slideshow.slides ?? [];
      const restIndex = slides.indexOf(this.#restSlide());
      const hoverIndex = restIndex + 1;
      if (restIndex === -1 || hoverIndex >= slides.length) return;

      slideshow.select(hoverIndex, undefined, { animate: false });
      this.#prefetchNextSlideImage();
    }

    this.#playHoverVideo();
  }

  /**
   * Resets the image to the rest slide.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (this.closest('[data-no-hover-preview]')) return;
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    this.#pauseHoverVideo();

    if (!slideshow || typeof slideshow.select !== 'function') return;

    this.#resetVariant();
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    slideshow.select(0, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    this.parentProductCard = this.closest('product-card');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      // Only keep ?variant= when a size filter is active on the collection —
      // otherwise the PDP should open with no size pre-selected.
      if (this.parentProductCard.linksWithVariant) {
        currentUrl.searchParams.set('variant', this.pendingVariantId);
      } else {
        currentUrl.searchParams.delete('variant');
      }
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();

      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      this.pendingVariantId = firstAvailableVariantId;

      this.fetchUpdatedSection(requestUrl);
      return;
    }

    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
